import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Share2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import FavoriteButton from "@/components/FavoriteButton";

function ShareIconButton({ book }) {
  const [copied, setCopied] = useState(false);

  const onShare = async (e) => {
    // Stop the Link click bubbling — don't navigate to the book page
    e.preventDefault();
    e.stopPropagation();

    const url = `${window.location.origin}/books/${book.id}`;
    const shareData = {
      title: book.title,
      text: book.subtitle
        ? `${book.title} — ${book.subtitle}`
        : `${book.title} by M.K. Bunch`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Link copied — ready to paste!");
        setTimeout(() => setCopied(false), 1600);
      }
    } catch { /* user cancelled */ }
  };

  return (
    <button
      type="button"
      onClick={onShare}
      aria-label={`Share ${book.title}`}
      className="absolute top-3 right-3 p-2 rounded-full transition-transform hover:-translate-y-0.5"
      style={{
        background: "rgba(255,255,255,0.94)",
        border: "1px solid #F0D9E8",
        color: "#2D3055",
        boxShadow: "0 8px 18px rgba(45,48,85,0.10)",
      }}
      data-testid={`book-card-share-${book.id}`}
    >
      {copied ? <Check size={16} style={{ color: "#5B8DEF" }} /> : <Share2 size={16} />}
    </button>
  );
}

export default function Books() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/books").then(({ data }) => {
      setBooks(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="books-page">
      <span className="font-caveat text-3xl" style={{ color: "#FF6B9D" }}>the full library</span>
      <h1 className="font-fredoka text-5xl sm:text-6xl" style={{ color: "#2D3055" }}>
        The Books
      </h1>
      <p className="mt-4 max-w-2xl text-lg" style={{ color: "#4A4E6D" }}>
        Every story here is a small hug in book form — written to be read together, giggled through,
        and returned to again and again.
      </p>

      {loading && <p className="mt-10 font-nunito" style={{ color: "#7E82A1" }}>Turning the pages…</p>}

      {!loading && books.length === 0 && (
        <div className="mt-10 paper-card p-10 text-center" data-testid="books-empty">
          <p className="font-fredoka text-2xl" style={{ color: "#2D3055" }}>Stories being written…</p>
          <p className="mt-2" style={{ color: "#4A4E6D" }}>Check back soon for new little wonders.</p>
        </div>
      )}

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {books.map((b, idx) => (
          <Link
            key={b.id}
            to={`/books/${b.id}`}
            className="warm-card p-5 fade-up book-tilt relative"
            style={{ animationDelay: `${idx * 80}ms` }}
            data-testid={`book-card-${b.id}`}
          >
            <ShareIconButton book={b} />
            <div className="absolute top-3 left-3 z-10">
              <FavoriteButton bookId={b.id} variant="chip" />
            </div>
            <div
              className="rounded-2xl overflow-hidden mb-4 aspect-[3/4] flex items-center justify-center"
              style={{ background: "linear-gradient(160deg,#FFF3F8 0%,#FFFFFF 60%,#F0EBFF 100%)" }}
            >
              <img src={b.cover_image_url} alt={b.title} className="w-full h-full object-contain" />
            </div>
            <p className="font-caveat text-lg" style={{ color: "#FF6B9D" }}>{b.age_range}</p>
            <h3 className="font-fredoka text-2xl mt-1" style={{ color: "#2D3055" }}>{b.title}</h3>
            {b.subtitle && <p className="text-sm mt-1 font-caveat" style={{ color: "#6EC4E8" }}>{b.subtitle}</p>}
            <p className="mt-2 text-sm line-clamp-3" style={{ color: "#4A4E6D" }}>{b.description}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
